import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Broadcast, ArrowsClockwise, CaretUp, CaretDown, MinusSquare } from "@phosphor-icons/react";
import { api, fmtNum } from "@/api";
import { Button } from "@/components/ui/button";

const MODES = ["scalp", "swing", "position", "macro"];
const ALL_MODES_KEY = "all";

export function UniverseCoverage({ onSelect }) {
  const [mode, setMode] = useState("swing");
  const [sortKey, setSortKey] = useState("best_score");
  const [sortDir, setSortDir] = useState("desc");
  const [filter, setFilter] = useState("");

  const isAllModes = mode === ALL_MODES_KEY;

  const singleQ = useQuery({
    queryKey: ["pair-coverage", mode],
    queryFn: () => api.pairsCoverage(mode, 150),
    refetchInterval: 90_000,
    enabled: !isAllModes,
  });

  // Multi-mode: pulls scores across all 4 modes at once. Server aggregates the
  // 4 individually-cached per-mode responses so a warm hit is ~50ms.
  const multiQ = useQuery({
    queryKey: ["pair-coverage-multi"],
    queryFn: () => api.pairsCoverageMulti(150),
    refetchInterval: 120_000,
    enabled: isAllModes,
  });

  const q = isAllModes ? multiQ : singleQ;

  const rows = useMemo(() => {
    if (isAllModes) {
      const rs = multiQ.data?.rows || [];
      const f = filter.trim().toUpperCase();
      const filtered = f ? rs.filter((r) => r.symbol?.includes(f)) : rs;
      const enriched = filtered.map((r) => {
        const m = r.modes || {};
        return {
          ...r,
          scalp_score: m.scalp?.best || 0,
          swing_score: m.swing?.best || 0,
          position_score: m.position?.best || 0,
          macro_score: m.macro?.best || 0,
        };
      });
      enriched.sort((a, b) => {
        const av = a[sortKey] ?? a.best_score_overall ?? 0;
        const bv = b[sortKey] ?? b.best_score_overall ?? 0;
        const cmp = (av > bv) - (av < bv);
        return sortDir === "desc" ? -cmp : cmp;
      });
      return enriched;
    }
    const rs = (singleQ.data?.rows || []).filter((r) => !r.error);
    const f = filter.trim().toUpperCase();
    const filtered = f ? rs.filter((r) => r.symbol?.includes(f)) : rs;
    filtered.sort((a, b) => {
      const av = a[sortKey] ?? 0;
      const bv = b[sortKey] ?? 0;
      const cmp = (av > bv) - (av < bv);
      return sortDir === "desc" ? -cmp : cmp;
    });
    return filtered;
  }, [singleQ.data, multiQ.data, sortKey, sortDir, filter, isAllModes]);

  const setSort = (k) => {
    if (sortKey === k) {
      setSortDir(sortDir === "desc" ? "asc" : "desc");
    } else {
      setSortKey(k);
      setSortDir("desc");
    }
  };

  return (
    <div className="border border-white/10 bg-black" data-testid="universe-coverage">
      <div className="px-4 py-2 border-b border-white/10 bg-[#0f0f0f] flex items-center gap-2 flex-wrap">
        <Broadcast size={13} className="text-[#F5C518]" />
        <span className="font-display text-[11px] tracking-widest uppercase text-white/70">
          Universe Coverage
        </span>
        <div className="ml-2 flex items-center gap-1">
          {[...MODES, ALL_MODES_KEY].map((m) => (
            <button
              key={m}
              data-testid={`coverage-mode-${m}`}
              onClick={() => {
                setMode(m);
                if (m === ALL_MODES_KEY) setSortKey("best_score_overall");
                else setSortKey("best_score");
              }}
              className={`px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest border ${
                mode === m
                  ? "border-[#F5C518]/60 text-[#F5C518] bg-[#F5C518]/10"
                  : "border-white/10 text-white/50 hover:text-white"
              }`}
            >
              {m === ALL_MODES_KEY ? "All Modes" : m}
            </button>
          ))}
        </div>
        <input
          data-testid="coverage-filter"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="filter symbol"
          className="bg-black border border-white/10 px-2 py-0.5 text-white font-mono text-[11px] w-32 ml-2"
        />
        <span className="ml-auto font-mono text-[10px] text-white/40">
          {(isAllModes ? multiQ.data?.count : singleQ.data?.count) ?? 0} pairs
        </span>
        <Button
          data-testid="coverage-refresh"
          onClick={() => q.refetch()}
          variant="outline"
          size="sm"
          className="rounded-none border-white/15 bg-transparent hover:bg-white/5 text-white h-6"
        >
          <ArrowsClockwise size={11} className={q.isFetching ? "animate-spin" : ""} />
        </Button>
      </div>

      <div className="overflow-auto max-h-[420px]">
        <table className="w-full text-left">
          <thead className="text-[10px] font-mono uppercase tracking-widest text-white/40 sticky top-0 bg-[#0f0f0f] border-b border-white/10 z-10">
            {!isAllModes && (
              <tr>
                <Th sortable label="Symbol" k="symbol" sortKey={sortKey} sortDir={sortDir} onClick={setSort} />
                <Th label="Src" k="source" sortKey={sortKey} sortDir={sortDir} onClick={setSort} sortable align="left" />
                <Th sortable label="ADX" k="adx" sortKey={sortKey} sortDir={sortDir} onClick={setSort} align="right" />
                <Th sortable label="Regime" k="volatility_regime" sortKey={sortKey} sortDir={sortDir} onClick={setSort} align="right" />
                <Th sortable label="Long" k="long_score" sortKey={sortKey} sortDir={sortDir} onClick={setSort} align="right" />
                <Th sortable label="Short" k="short_score" sortKey={sortKey} sortDir={sortDir} onClick={setSort} align="right" />
                <Th sortable label="Best" k="best_score" sortKey={sortKey} sortDir={sortDir} onClick={setSort} align="right" />
                <Th sortable label="Price" k="price" sortKey={sortKey} sortDir={sortDir} onClick={setSort} align="right" />
              </tr>
            )}
            {isAllModes && (
              <tr>
                <Th sortable label="Symbol" k="symbol" sortKey={sortKey} sortDir={sortDir} onClick={setSort} />
                <Th sortable label="Scalp" k="scalp_score" sortKey={sortKey} sortDir={sortDir} onClick={setSort} align="right" />
                <Th sortable label="Swing" k="swing_score" sortKey={sortKey} sortDir={sortDir} onClick={setSort} align="right" />
                <Th sortable label="Position" k="position_score" sortKey={sortKey} sortDir={sortDir} onClick={setSort} align="right" />
                <Th sortable label="Macro" k="macro_score" sortKey={sortKey} sortDir={sortDir} onClick={setSort} align="right" />
                <Th sortable label="Best" k="best_score_overall" sortKey={sortKey} sortDir={sortDir} onClick={setSort} align="right" />
                <Th sortable label="Mode" k="best_mode" sortKey={sortKey} sortDir={sortDir} onClick={setSort} align="left" />
                <Th sortable label="Price" k="price" sortKey={sortKey} sortDir={sortDir} onClick={setSort} align="right" />
              </tr>
            )}
          </thead>
          <tbody>
            {!isAllModes && rows.map((r) => {
              const isElig = r.long_eligible || r.short_eligible;
              return (
                <tr
                  key={r.symbol}
                  onClick={() => onSelect && onSelect(r.symbol, r.best_direction, mode)}
                  className={`border-b border-white/5 cursor-pointer hover:bg-white/5 ${
                    isElig ? "bg-[#00FF66]/5" : ""
                  }`}
                  data-testid={`coverage-row-${r.symbol}`}
                >
                  <td className="px-3 py-1 font-mono text-[11px] text-white">
                    {r.symbol.replace("USDT", "")}
                    {isElig && <span className="ml-1.5 text-[9px] text-[#00FF66]">●</span>}
                  </td>
                  <td className="px-2 py-1 font-mono text-[10px] uppercase">
                    <span className={r.source === "okx" ? "text-white/50" : "text-[#F5C518]/70"}>
                      {r.source}
                    </span>
                  </td>
                  <td className="px-2 py-1 font-mono text-[11px] text-right">
                    <span style={{ color: r.adx >= 25 ? "#00FF66" : r.adx >= 20 ? "#F5C518" : "#FF3B30" }}>
                      {r.adx?.toFixed?.(1) ?? "-"}
                    </span>
                  </td>
                  <td className="px-2 py-1 font-mono text-[10px] text-right uppercase">
                    <span
                      style={{
                        color:
                          r.volatility_regime === "high"
                            ? "#FF3B30"
                            : r.volatility_regime === "low"
                            ? "#00FF66"
                            : "#F5C518",
                      }}
                    >
                      {r.volatility_regime || "-"}
                    </span>
                  </td>
                  <ScoreCell v={r.long_score} elig={r.long_eligible} tint="#00FF66" />
                  <ScoreCell v={r.short_score} elig={r.short_eligible} tint="#FF3B30" />
                  <td className="px-2 py-1 font-mono text-[11px] font-bold text-right">
                    <span
                      style={{
                        color: r.best_score >= 70 ? "#00FF66" : r.best_score >= 60 ? "#F5C518" : "#FF3B30",
                      }}
                    >
                      {r.best_score ? Math.round(r.best_score) : "-"}
                    </span>
                  </td>
                  <td className="px-2 py-1 font-mono text-[10px] text-right text-white/60">
                    {r.price ? fmtNum(r.price) : "-"}
                  </td>
                </tr>
              );
            })}
            {isAllModes && rows.map((r) => {
              const bmColor =
                r.best_direction === "long" ? "#00FF66" : r.best_direction === "short" ? "#FF3B30" : "#888";
              return (
                <tr
                  key={r.symbol}
                  onClick={() => onSelect && onSelect(r.symbol, r.best_direction, r.best_mode || "swing")}
                  className="border-b border-white/5 cursor-pointer hover:bg-white/5"
                  data-testid={`coverage-multi-row-${r.symbol}`}
                >
                  <td className="px-3 py-1 font-mono text-[11px] text-white">
                    {r.symbol.replace("USDT", "")}
                  </td>
                  <ModeScoreCell md={r.modes?.scalp} />
                  <ModeScoreCell md={r.modes?.swing} />
                  <ModeScoreCell md={r.modes?.position} />
                  <ModeScoreCell md={r.modes?.macro} />
                  <td className="px-2 py-1 font-mono text-[11px] font-bold text-right">
                    <span
                      style={{
                        color:
                          r.best_score_overall >= 70
                            ? "#00FF66"
                            : r.best_score_overall >= 60
                            ? "#F5C518"
                            : "#FF3B30",
                      }}
                    >
                      {r.best_score_overall ? Math.round(r.best_score_overall) : "-"}
                    </span>
                  </td>
                  <td className="px-2 py-1 font-mono text-[10px] text-left uppercase" style={{ color: bmColor }}>
                    {r.best_mode || "-"} {r.best_direction ? `· ${r.best_direction}` : ""}
                  </td>
                  <td className="px-2 py-1 font-mono text-[10px] text-right text-white/60">
                    {r.price ? fmtNum(r.price) : "-"}
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-center text-white/40 font-mono text-[11px]">
                  {q.isLoading ? "Scanning universe..." : "No matches."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Th({ label, k, sortKey, sortDir, onClick, align = "left", sortable = false }) {
  const active = sortKey === k;
  return (
    <th
      className={`px-2 py-1.5 tracking-widest ${sortable ? "cursor-pointer hover:text-white" : ""} ${
        align === "right" ? "text-right" : "text-left"
      }`}
      onClick={sortable ? () => onClick(k) : undefined}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active && sortable && (sortDir === "desc" ? <CaretDown size={9} /> : <CaretUp size={9} />)}
        {!active && sortable && <MinusSquare size={9} className="opacity-30" />}
      </span>
    </th>
  );
}

function ScoreCell({ v, elig, tint }) {
  if (v == null || v === 0) return <td className="px-2 py-1 font-mono text-[10px] text-right text-white/25">-</td>;
  return (
    <td className="px-2 py-1 font-mono text-[11px] text-right">
      <span style={{ color: elig ? tint : v >= 60 ? "#fff" : "rgba(255,255,255,0.45)" }}>
        {Math.round(v)}
        {elig && <span className="ml-0.5 text-[8px]">●</span>}
      </span>
    </td>
  );
}

function ModeScoreCell({ md }) {
  if (!md) return <td className="px-2 py-1 text-right font-mono text-[10px] text-white/25">-</td>;
  const best = md.best || 0;
  const dir = md.best_direction;
  const elig = dir === "long" ? md.long_eligible : md.short_eligible;
  const color = dir === "long" ? "#00FF66" : "#FF3B30";
  return (
    <td className="px-2 py-1 font-mono text-[11px] text-right">
      <span style={{ color: elig ? color : best >= 60 ? "#fff" : "rgba(255,255,255,0.45)" }}>
        {best ? Math.round(best) : "-"}
        {best > 0 && (
          <span className="ml-0.5 text-[8px]" style={{ color }}>
            {dir === "long" ? "L" : "S"}
          </span>
        )}
        {elig && <span className="ml-0.5 text-[8px]">●</span>}
      </span>
    </td>
  );
}
